import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import { HorizontalSpacer } from "../atoms/spacers"
import SmallButton from "../atoms/buttons/SmallButton"
import TextButton from "../atoms/buttons/TextButton"
import { ArrowDropDownIcon } from "../atoms/icons/ArrowDropDownIcon"
import { ArrowDropUpIcon } from "../atoms/icons/ArrowDropUpIcon"
import { CrossIcon } from "../atoms/icons/CrossIcon"
import * as Sentry from "@sentry/react"
import { useRemoveOrgJoinRequest } from "../../hooks/useRemoveOrganizationJoinRequest"
import { MediaQuery } from "../../utils/design-helpers"
import Body from "../atoms/typography/Body"
import { ApolloError } from "@apollo/client"
import { captureExceptionWithContext } from "../../utils/sentry"

export interface PendingInviteApiType {
  id: number | string
  initialGroup?: {
    id: number | string
    name: string
    organizationGroup: { id: any; name: string }
  }
  organization?: { id: number | string; name: string }
  toUser: string
}

interface Props {
  pendingInvites: Array<PendingInviteApiType>
}

const CardContainer = styled.div<{ open: boolean }>`
  padding: ${(props) => props.theme.spacings.large};
  border-radius: ${(props) => props.theme.borders.softRadius};
  background-color: ${(props) => (props.open ? props.theme.colors.greyLight : "transparent")};
`

const TitleSection = styled.div`
  ${MediaQuery.XS} {
    display: flex;
    justify-content: center;
  }
`

const PendingInvitesCard: React.FunctionComponent<Props> = ({ pendingInvites }) => {
  const [open, setOpen] = React.useState(false)

  const onCompletedRemoveJoinReq = () => {
    console.log("Removed org join request")
  }
  const onErrorRemoveJoinReq = (apolloError: ApolloError) => {
    captureExceptionWithContext(apolloError, "error removing org join request")
  }
  const { removeOrgJoinRequest } = useRemoveOrgJoinRequest({
    onCompleted: onCompletedRemoveJoinReq,
    onError: onErrorRemoveJoinReq,
  })
  const onRemoveJoinOrgRequest = async (id: number | string) => {
    try {
      await removeOrgJoinRequest({
        variables: {
          id: id,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error removing org join request")
    }
  }

  return (
    <CardContainer open={open}>
      <TitleSection>
        <TextButton
          title="Pending invites"
          icon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          onClick={() => setOpen(!open)}
        />
      </TitleSection>
      <HorizontalSpacer spacing={1} />
      <SlideOutHeight isVisible={open}>
        <HorizontalSpacer spacing={2} />
        {pendingInvites?.map((invite: PendingInviteApiType) => (
          <div key={invite.id}>
            <Row align="center" style={{ padding: 15 }}>
              <Col md={4} xs={10}>
                <Body> {invite.toUser}</Body>
              </Col>
              <Visible xs>
                <Col xs={2} onClick={() => onRemoveJoinOrgRequest(invite.id)}>
                  <CrossIcon />
                </Col>
              </Visible>
              <Col md={4} xs={10}>
                <Body>
                  Invited to: {invite.organization && `${invite.organization.name}`}{" "}
                  {invite.initialGroup &&
                    ` - ${invite.initialGroup.organizationGroup.name} (${invite.initialGroup.name})`}
                </Body>
              </Col>
              <Hidden xs>
                <Col md={4} style={{ display: "flex", justifyContent: "flex-end" }}>
                  <SmallButton
                    title="Cancel invite"
                    icon={<CrossIcon />}
                    onClick={() => onRemoveJoinOrgRequest(invite.id)}
                  />
                </Col>
              </Hidden>
            </Row>
            <HorizontalSpacer spacing={1.375} />
          </div>
        ))}
      </SlideOutHeight>
    </CardContainer>
  )
}

export default PendingInvitesCard
